import { cn } from '@/lib/utils';
import { Check, Eye, EyeOff, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Translator, { useTranslation } from 'components/i18n/Translator';

import Alert from './Alert';
import { ProviderButton } from './ProviderButton';

interface Props {
  error?: string;
  providers: string[];
  onSignup?: (name: string, email: string, password: string) => Promise<any>;
  onOAuthSignIn?: (provider: string) => Promise<any>;
}

interface FormValues {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface PasswordStrength {
  minLength: boolean;
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
  special: boolean;
}

export function SignupForm({
  providers,
  onSignup,
  onOAuthSignIn,
  error
}: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState(error);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    minLength: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });

  const oAuthReady = onOAuthSignIn && providers.length;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, touchedFields }
  } = useForm<FormValues>({
    defaultValues: {
      name: '',
      email: '',
      password: '',
      confirmPassword: ''
    }
  });

  const password = watch('password');
  const confirmPassword = watch('confirmPassword');

  // Update password strength indicators
  useEffect(() => {
    if (password) {
      setPasswordStrength({
        minLength: password.length >= 12,
        uppercase: /[A-Z]/.test(password),
        lowercase: /[a-z]/.test(password),
        number: /\d/.test(password),
        special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
      });
    }
  }, [password]);

  useEffect(() => {
    setErrorState(error);
  }, [error]);

  const onSubmit = async (data: FormValues) => {
    if (!onSignup) return;

    // Validate password confirmation
    if (data.password !== data.confirmPassword) {
      setErrorState(t('auth.signup.errors.passwordsMismatch'));
      return;
    }

    // Check password strength
    const allStrengthChecks = Object.values(passwordStrength).every(Boolean);
    if (!allStrengthChecks) {
      setErrorState(t('auth.signup.errors.passwordRequirements'));
      return;
    }

    setLoading(true);
    setErrorState(undefined);

    try {
      await onSignup(data.name, data.email, data.password);
    } catch (err) {
      if (err instanceof Error) {
        setErrorState(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const StrengthIndicator = ({
    check,
    label
  }: {
    check: boolean;
    label: string;
  }) => (
    <div
      className={cn(
        'flex items-center gap-2 text-sm',
        check ? 'text-green-600' : 'text-gray-400'
      )}
    >
      {check ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
      <span>{label}</span>
    </div>
  );

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={cn('flex flex-col gap-6')}
    >
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="text-2xl font-bold">
          <Translator path="auth.signup.title" />
        </h1>
        <p className="text-muted-foreground">
          <Translator path="auth.signup.description" />
        </p>
      </div>

      {errorState && <Alert variant="error">{errorState}</Alert>}

      <div className="grid gap-6">
        {/* Name Field */}
        <div className="grid gap-2">
          <Label htmlFor="name">
            <Translator path="auth.signup.form.name.label" />
          </Label>
          <Input
            id="name"
            type="text"
            disabled={loading}
            autoFocus
            placeholder={t('auth.signup.form.name.placeholder')}
            {...register('name', {
              required: t('auth.signup.form.name.required'),
              minLength: {
                value: 2,
                message: t('auth.signup.form.name.minLength')
              }
            })}
            className={cn(
              touchedFields.name &&
                errors.name &&
                'border-black dark:border-white'
            )}
          />
          {touchedFields.name && errors.name && (
            <p className="text-sm text-destructive">{errors.name.message}</p>
          )}
        </div>

        {/* Email Field */}
        <div className="grid gap-2">
          <Label htmlFor="email">
            <Translator path="auth.signup.form.email.label" />
          </Label>
          <Input
            id="email"
            type="email"
            disabled={loading}
            placeholder={t('auth.signup.form.email.placeholder')}
            {...register('email', {
              required: t('auth.signup.form.email.required'),
              pattern: {
                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                message: t('auth.signup.form.email.invalid')
              }
            })}
            className={cn(
              touchedFields.email &&
                errors.email &&
                'border-black dark:border-white'
            )}
          />
          {touchedFields.email && errors.email && (
            <p className="text-sm text-destructive">{errors.email.message}</p>
          )}
        </div>

        {/* Password Field */}
        <div className="grid gap-2">
          <Label htmlFor="password">
            <Translator path="auth.signup.form.password.label" />
          </Label>
          <div className="relative">
            <Input
              id="password"
              disabled={loading}
              type={showPassword ? 'text' : 'password'}
              placeholder={t('auth.signup.form.password.placeholder')}
              {...register('password', {
                required: t('auth.signup.form.password.required')
              })}
              className={cn(
                touchedFields.password &&
                  errors.password &&
                  'border-black dark:border-white'
              )}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Password Strength Indicators */}
          {password && (
            <div className="mt-2 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium mb-2">
                <Translator path="auth.signup.passwordRequirements.title" />
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                <StrengthIndicator
                  check={passwordStrength.minLength}
                  label={t('auth.signup.passwordRequirements.minLength')}
                />
                <StrengthIndicator
                  check={passwordStrength.uppercase}
                  label={t('auth.signup.passwordRequirements.uppercase')}
                />
                <StrengthIndicator
                  check={passwordStrength.lowercase}
                  label={t('auth.signup.passwordRequirements.lowercase')}
                />
                <StrengthIndicator
                  check={passwordStrength.number}
                  label={t('auth.signup.passwordRequirements.number')}
                />
                <StrengthIndicator
                  check={passwordStrength.special}
                  label={t('auth.signup.passwordRequirements.special')}
                />
              </div>
            </div>
          )}

          {touchedFields.password && errors.password && (
            <p className="text-sm text-destructive">
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Confirm Password Field */}
        <div className="grid gap-2">
          <Label htmlFor="confirmPassword">
            <Translator path="auth.signup.form.confirmPassword.label" />
          </Label>
          <div className="relative">
            <Input
              id="confirmPassword"
              disabled={loading}
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder={t('auth.signup.form.confirmPassword.placeholder')}
              {...register('confirmPassword', {
                required: t('auth.signup.form.confirmPassword.required')
              })}
              className={cn(
                touchedFields.confirmPassword &&
                  (errors.confirmPassword ||
                    (confirmPassword && password !== confirmPassword)) &&
                  'border-black dark:border-white'
              )}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </Button>
          </div>

          {confirmPassword && password !== confirmPassword && (
            <p className="text-sm text-destructive">
              <Translator path="auth.signup.form.confirmPassword.mismatch" />
            </p>
          )}

          {touchedFields.confirmPassword && errors.confirmPassword && (
            <p className="text-sm text-destructive">
              {errors.confirmPassword.message}
            </p>
          )}
        </div>

        <Button
          type="submit"
          className="w-full"
          disabled={
            loading ||
            !Object.values(passwordStrength).every(Boolean) ||
            password !== confirmPassword
          }
        >
          {loading
            ? t('auth.signup.actions.creating')
            : t('auth.signup.actions.create')}
        </Button>

        {onSignup && oAuthReady ? (
          <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
            <span className="relative z-10 bg-background px-2 text-muted-foreground">
              <Translator path="auth.signup.actions.continueWith" />
            </span>
          </div>
        ) : null}

        {oAuthReady ? (
          <div className="grid gap-2">
            {providers.map((provider, index) => (
              <ProviderButton
                key={`provider-${index}`}
                provider={provider}
                onClick={() => onOAuthSignIn?.(provider)}
              />
            ))}
          </div>
        ) : null}
      </div>
    </form>
  );
}
